import React, { RefObject, useEffect, useMemo } from "react";
import * as THREE from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";

import { usePrevious } from "@chakra-ui/react";
import { useGLTF } from "@react-three/drei";

import { DisplaySettings } from "./types";

interface ThreeViewerProps {
  /** URL of the 3D asset to load and display */
  assetUrl: string;
  /** Reference to orbit controls for camera manipulation */
  orbitRef: RefObject<OrbitControlsImpl>;
  /** Display settings controlling visual appearance */
  settings: DisplaySettings;
  /** Callback fired when wireframe processing is complete */
  onWireframeProcessed: () => void;
}

/**
 * Core Three.js viewer component that handles 3D model loading and rendering
 *
 * Responsibilities:
 * - Loads GLTF/GLB 3D models using useGLTF hook
 * - Applies display settings to materials (contrast, wireframe, shadows)
 * - Manages material and geometry disposal for memory efficiency
 * - Auto-fits models to viewport with proper scaling and centering
 * - Preserves original PBR material properties when possible
 * - Adds wireframe overlay when enabled
 * - Renders shadow-receiving ground plane
 * - Handles wireframe processing callbacks for loading states
 *
 * @param props - Component props with asset URL, controls ref, and settings
 * @returns JSX element containing the 3D scene primitives
 */
export function ThreeViewer({
  assetUrl,
  orbitRef,
  settings,
  onWireframeProcessed,
}: ThreeViewerProps) {
  const gltf = useGLTF(assetUrl);
  const previousGltf = usePrevious(gltf);
  const previousWireframe = usePrevious(settings.wireframe);
  const previousWireframeOnly = usePrevious(settings.wireframeOnly);

  // Reset camera position when new model loads (CameraController will handle proper positioning)
  useEffect(() => {
    // Let CameraController handle the positioning for new models
    // This just ensures orbit controls are reset to defaults
    if (orbitRef.current) {
      orbitRef.current.reset();
    }
  }, [gltf, orbitRef]);

  // Clean up previous model resources to prevent memory leaks
  useEffect(() => {
    if (previousGltf) {
      previousGltf.scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.material.dispose();
          child.geometry.dispose();
        }
      });
    }
  }, [gltf, previousGltf, orbitRef]);

  // Theme-based colors
  const themeColors = useMemo(() => {
    if (settings.theme === "dark") {
      return {
        shadow: new THREE.Color("#2a3a5a"),
        wireframe: new THREE.Color(
          settings.wireframeOnly ? "#ffffff" : "#404040"
        ),
      };
    } else {
      return {
        shadow: new THREE.Color("#5a4a3a"),
        wireframe: new THREE.Color("#000000"),
      };
    }
  }, [settings.theme, settings.wireframeOnly]);

  /**
   * Processes the loaded GLTF scene with display settings applied
   *
   * Processing steps:
   * 1. Clones the original scene to avoid modifying the cached version
   * 2. Traverses all mesh children to apply material settings
   * 3. Preserves original PBR values (metalness, roughness) when available
   * 4. Applies contrast adjustment using a subtle algorithm
   * 5. Enables shadow casting/receiving based on settings
   * 6. Adds wireframe overlay if enabled (can be expensive for complex models)
   * 7. Auto-fits the model to a standardized size and centers it
   *
   * @returns Processed Three.js scene object ready for rendering
   */
  const scene = useMemo(() => {
    const { scene: initialScene } = gltf;
    const scene = initialScene.clone();

    scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const material = child.material.clone() as THREE.MeshStandardMaterial;
        if (material) {
          if (settings.wireframeOnly) {
            // Make material invisible for wireframe-only mode
            material.transparent = true;
            material.opacity = 0;
          } else {
            // Apply normal material settings
            material.envMapIntensity = settings.environmentIntensity;

            // Preserve existing PBR values, only set defaults if undefined
            if (material.metalness === undefined) material.metalness = 0;
            if (material.roughness === undefined) material.roughness = 0.8;

            // Apply subtle contrast adjustment to avoid over-saturation
            if (material.color && settings.contrast !== 1) {
              const factor = 1 + (settings.contrast - 1) * 0.3;
              material.color.multiplyScalar(factor);
              material.color.addScalar(-0.5 * (factor - 1));
            }

            // Reset transparency if it was set for wireframe-only mode
            if (material.transparent && material.opacity === 0) {
              material.transparent = false;
              material.opacity = 1;
            }
          }

          material.needsUpdate = true;
        }

        child.material = material;
        child.castShadow = settings.enableShadows;
        child.receiveShadow = settings.enableShadows;

        // Add wireframe overlay if enabled or in wireframe-only mode
        if (settings.wireframe || settings.wireframeOnly) {
          const wireframeOpacity = settings.wireframeOnly ? 1.0 : 0.8;

          const wireframe = new THREE.LineSegments(
            new THREE.WireframeGeometry(child.geometry),
            new THREE.LineBasicMaterial({
              color: themeColors.wireframe,
              transparent: true,
              opacity: wireframeOpacity,
            })
          );
          child.add(wireframe);
        }
      }
    });

    // Auto-fit model to viewport with consistent scaling
    const box = new THREE.Box3().setFromObject(scene);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 2 / maxDim;
    scene.scale.set(scale, scale, scale);
    scene.position.set(-center.x * scale, -center.y * scale, -center.z * scale);

    // Expose bounding information for camera positioning
    scene.name = "ModelRoot";
    scene.userData.boundingBox = {
      size: size.clone().multiplyScalar(scale), // size after scaling
      center: new THREE.Vector3(0, 0, 0), // after centering, model root is at origin
    };

    return scene;
  }, [
    gltf,
    settings.enableShadows,
    settings.wireframe,
    settings.wireframeOnly,
    settings.environmentIntensity,
    settings.contrast,
    themeColors.wireframe,
  ]);

  // Notify when wireframe processing is complete
  useEffect(() => {
    if (
      previousWireframe !== settings.wireframe ||
      previousWireframeOnly !== settings.wireframeOnly
    ) {
      onWireframeProcessed();
    }
  }, [
    settings.wireframe,
    settings.wireframeOnly,
    previousWireframe,
    previousWireframeOnly,
    onWireframeProcessed,
  ]);

  // Clean up previous scene resources
  const previousScene = usePrevious(scene);
  useEffect(() => {
    if (previousScene) {
      previousScene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.material.dispose();
          child.geometry.dispose();
        }
      });
    }
  }, [previousScene, scene]);

  return (
    <>
      <primitive object={scene} />
      {/* Invisible ground plane for shadow receiving */}
      {settings.enableShadows && (
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, -1, 0]}
          receiveShadow
        >
          <planeGeometry args={[40, 40]} />
          <shadowMaterial
            transparent
            opacity={0.35}
            color={themeColors.shadow}
          />
        </mesh>
      )}
    </>
  );
}
